import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Card, Col, Form, Row } from "react-bootstrap";
import { Box, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, Switch, TextField } from "@mui/material";
import { Button as ButtonCustom } from "../../../../components/Formularios/Buttons/Button";
import { mdiCheckCircle, mdiKeyboardReturn } from "@mdi/js";
import ModalLoading from "../../../../components/Formularios/Modal/ModalLoading";
import ModalUploadImage from "../../../../components/Formularios/Modal/ModalUpload";

import { Controller, useForm } from "react-hook-form";
import { AgenteInterface } from "../../../../types/AgenteInterface";
import { endpointPrincipal } from "../../../../api/endPoints/endpointPrincipal/endpointPrincipal";

interface FormAgenteProps {
  idagente?: string;
  acao?: string;
}

const FormAgente = ({ idagente, acao }: FormAgenteProps) => {
  const { register, handleSubmit, setValue, control } = useForm<AgenteInterface>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(false);
  const [showUpload, setShowUpload] = useState<boolean>(false);
  const [situacao, setSituacao] = useState<number>(1);
  const [funcoes, setFuncoes] = useState<any[]>([]);
  const [grupos, setGrupos] = useState<any[]>([]);

  const carregarAgentePorId = async () => {
    setLoading(true);
    try {
      await carregarOpcoesSelect();
      
      const { data } = await endpointPrincipal.get(`agentes/${idagente}`);
      
      setValue("id_agente", data.id_agente);
      setValue("nome", data.nome);
      setValue("email", data.email);
      setValue("funcao", data.funcao);
      setValue("grupo", data.grupo);
      setValue("avatar", data.avatar);
      setValue("ultimo_acesso", data.ultimo_acesso);
      setSituacao(data.situacao || 1);
    } catch (error) {
      toast.error("Falha ao carregar dados. Motivo: " + error);
    } finally {
      setLoading(false);
    }
  };

  const carregarOpcoesSelect = async () => {
    try {
      const [funcoesResponse, gruposResponse] = await Promise.all([
        endpointPrincipal.get('codigosistema/detalhes/FUNCOES'),
        endpointPrincipal.get('codigosistema/detalhes/GRUPOS')
      ]);
      
      setFuncoes(funcoesResponse.data);
      setGrupos(gruposResponse.data);
    } catch (error) {
      toast.error("Falha ao carregar opções dos campos. Motivo: " + error);
    }
  };

  useEffect(() => {
    if (idagente && idagente !== "novo") {
      carregarAgentePorId();
    } else {
      carregarOpcoesSelect();
    }
  }, [idagente]);

  const atualizarAgentePorId = async (data: AgenteInterface) => {
    setLoading(true);
    try {
      await endpointPrincipal.put(`agentes/${idagente}`, { ...data, situacao });
      toast.success(`Agente atualizado com sucesso`);
      setTimeout(() => navigate(-1), 2000);
    } catch (error) {
      toast.error("Falha ao atualizar agente. Motivo: " + error);
    } finally {
      setLoading(false);
    }
  };

  const incluirAgente = async (data: AgenteInterface) => {
    setLoading(true);
    try {
      await endpointPrincipal.post("agentes", { ...data, situacao });
      toast.success(`Agente adicionado com sucesso`);
      setTimeout(() => navigate(-1), 2000);
    } catch (error) {
      toast.error(`Erro ao adicionar agente`);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (data: AgenteInterface) => {
    if (idagente === "novo") {
      incluirAgente(data);
    } else {
      atualizarAgentePorId(data);
    }
  };

  return (
    <>
      <Card>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Card.Body>
            <Box sx={{ width: "100%" }}>
              <Row>
                <Col sm={3}>
                  <TextField
                    sx={{
                      width: "100%",
                      mb: 3,
                      "& .MuiInputBase-input.Mui-disabled": {
                        backgroundColor: "rgba(242, 242, 242, 0.50)"
                      }
                    }}
                    id="outlined-basic"
                    label="Código Agente"
                    size="small"
                    {...register("id_agente")}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    disabled
                  />
                </Col>
                <Col sm={6}>
                  <TextField
                    sx={{ width: "100%", mb: 3 }}
                    id="outlined-basic"
                    label="Nome"
                    size="small"
                    {...register("nome", { required: true })}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    disabled={acao === "Visualizar"}
                  />
                </Col>
                <Col sm={3}>
                  <label htmlFor="" className="form-label me-2">
                    Ativo
                  </label>
                  <Switch
                    checked={situacao === 1}
                    onChange={() => setSituacao(situacao === 1 ? 0 : 1)}
                    disabled={acao === "Visualizar"}
                  />
                </Col>
              </Row>
              <Row>
                <Col sm={6}>
                  <TextField
                    sx={{ width: "100%", mb: 3 }}
                    id="outlined-basic"
                    label="Email"
                    size="small"
                    {...register("email", { required: true })}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    disabled={acao === "Visualizar"}
                  />
                </Col>
                <Col sm={3}>
                  <FormControl fullWidth size="small" sx={{ mb: 3 }}>
                    <InputLabel>Função</InputLabel>
                    <Controller
                      name="funcao"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <Select
                          {...field}
                          label="Função"
                          disabled={acao === "Visualizar"}
                        >
                          <MenuItem value="">Selecione...</MenuItem>
                          {funcoes.map((funcao) => (
                            <MenuItem key={funcao.codigo} value={funcao.codigo}>
                              {funcao.descricao}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                  </FormControl>
                </Col>
                <Col sm={3}>
                  <FormControl fullWidth size="small" sx={{ mb: 3 }}>
                    <InputLabel>Grupo</InputLabel>
                    <Controller
                      name="grupo"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <Select
                          {...field}
                          label="Grupo"
                          disabled={acao === "Visualizar"}
                        >
                          <MenuItem value="">Selecione...</MenuItem>
                          {grupos.map((grupo) => (
                            <MenuItem key={grupo.codigo} value={grupo.codigo}>
                              {grupo.descricao}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                  </FormControl>
                </Col>
              </Row>
            </Box>
          </Card.Body>
          <Card.Footer>
            <div className="d-flex my-2 gap-1 justify-content-between">
              <ButtonCustom
                type="button"
                className="btn btn-light text-danger bg-white border-0"
                descricaoBotao="Voltar"
                icone={mdiKeyboardReturn}
                onclick={() => {
                  navigate(-1);
                }}
              />
              {acao !== "Visualizar" && (
                <ButtonCustom
                  type="submit"
                  className="btn btn-light text-info-emphasis bg-white border-0"
                  descricaoBotao="Salvar"
                  icone={mdiCheckCircle}
                />
              )}
            </div>
          </Card.Footer>
        </Form>
      </Card>
      <ModalUploadImage
        show={showUpload}
        label="Realizando Upload da Imagem ..."
      />
      <ModalLoading show={loading} label="Carregando dados do agente ..." />
    </>
  );
};

export default FormAgente; 